const download = (filename, data) => {
  const url = window.URL.createObjectURL(data);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  a.remove();
};

// Simple function that triggers a download in browser w/o using browser memory
export const triggerZipDownload = url => {
  const a = document.createElement('a');
  a.href = url;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

/**
 * @param {Res} httpResponse
 */
export const downloadFile = httpResponse => {
  const [, filename] =
    httpResponse.headers['content-disposition'].split('filename=');
  download(filename, httpResponse.data);
};

/**
 * Download files from S3 for printing and zip download
 * @param {string} url
 * @param {object } options
 * @param {string} options.filename File name used for download
 * @param {boolean} options.shouldPrint Send downloaded single PDFs to printer
 * @param {boolean} options.useStreaming Process larger files in chunks
 * @returns {Promise<Error|boolean>}
 */
export const downloadFromURL = async (url, options = {}) => {
  const { filename, shouldPrint = false, useStreaming = false } = options;

  if (!url) {
    return false;
  }

  try {
    const newUrl = new URL(url);

    // Default headers with an option to override/extend
    const fetchHeaders = {
      Accept: 'application/pdf,*/*',
      'Cache-Control': 'no-cache',
    };

    const response = await fetch(newUrl, {
      method: 'GET',
      headers: fetchHeaders,
    });

    if (!response.ok) {
      return new Error(`HTTP error! status: ${response.status}`);
    }

    let blob;

    let contentType = response.headers.get('content-type');
    const urlPath = newUrl.pathname.toLowerCase();
    if (urlPath.endsWith('.zip')) {
      contentType = 'application/zip';
    } else if (urlPath.endsWith('.pdf')) {
      contentType = 'application/pdf';
    }

    if (useStreaming) {
      // Use streaming for large files
      const reader = response.body.getReader();
      const chunks = [];

      let done = false;
      while (!done) {
        const result = await reader.read();
        done = result.done;
        if (!done) {
          chunks.push(result.value);
        }
      }

      // Combine chunks into a single Uint8Array
      const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
      const combinedArray = new Uint8Array(totalLength);
      let offset = 0;

      for (const chunk of chunks) {
        combinedArray.set(chunk, offset);
        offset += chunk.length;
      }

      blob = new Blob([combinedArray], { type: contentType });
    } else {
      // Simple blob conversion for smaller files
      const responseBlob = await response.blob();
      blob = new Blob([responseBlob], { type: contentType });
    }

    if (shouldPrint) {
      return await printPdfBlob(blob);
    } else {
      // Extract filename from headers if not provided
      let finalFilename = filename;

      if (!finalFilename) {
        const header = response.headers.get('content-disposition');
        finalFilename = header?.split('filename=')[1];
      }

      download(finalFilename, blob);
      return true;
    }
  } catch (error) {
    throw new Error(`Download/Print failed: ${error}`);
  }
};

/**
 * Prints a PDF blob using an iframe
 * @param {Blob} pdfBlob - PDF blob to print
 * @returns {Promise<boolean>} - Success status of the print operation
 */
const printPdfBlob = pdfBlob => {
  return new Promise(resolve => {
    if (!pdfBlob) {
      resolve(false);
      return false;
    }

    const blobUrl = URL.createObjectURL(pdfBlob);
    const iframe = document.createElement('iframe');

    iframe.style.position = 'absolute';
    iframe.style.top = '0';
    iframe.style.left = '0';
    iframe.style.width = '10px';
    iframe.style.height = '10px';
    iframe.style.border = '0';

    iframe.src = blobUrl;

    iframe.onload = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        resolve(true);
      } catch (error) {
        resolve(false);
      }
    };

    // Handle load errors
    iframe.onerror = () => {
      resolve(false);
    };

    // Add an iframe to the document to trigger loading
    document.body.appendChild(iframe);
  });
};
